<template>
    <div>
        <b-row>
            <b-col sm="3" xs="12">
                <h1>Kunder</h1>
            </b-col>
            <b-col sm="9" xs="12" class="text-right" style="margin: auto">
                <b-button :to="{ name: 'admin-manage-user-list' }" class="mr-2">
                    <font-awesome-icon icon="user" />  Användare
                </b-button>
                <b-button variant="primary" @click="addFromTemplate" v-show="isSuperAdmin"> Kopiera mall </b-button>
                <b-button variant="primary" class="ml-2" @click="add"> Lägg till </b-button>
            </b-col>
            <b-col md="7" sm="12" class="my-1">
                <b-form-group label-for="filter-input"
                              label-align-sm="right"
                              class="mb-0">
                    <b-input-group>
                        <b-form-input id="filter-input"
                                      v-model="filter"
                                      type="search"
                                      placeholder="Filtrera...." />
                        <b-input-group-append>
                            <b-button :disabled="!filter" @click="filter = ''">
                                Rensa
                            </b-button>
                        </b-input-group-append>
                    </b-input-group>
                </b-form-group>
            </b-col>

            <b-col sm="12" md="5" class="my-1">
                <b-form-group label="Per sida"
                              label-for="per-page-select"
                              label-cols-sm="6"
                              label-align-sm="right"
                              class="mb-0">
                    <PaginationPageSizeSelect id="per-page-select" v-model="perPage" />
                </b-form-group>
            </b-col>

            <b-col v-show="totalRows > perPage" cols="12" class="my-1 text-center">
                <b-pagination v-model="currentPage"
                              :total-rows="totalRows"
                              :per-page="perPage"
                              align="center"
                              class="my-0" />
            </b-col>
        </b-row>

        <!--TABLE-->
        <b-table :items="customers"
                 :fields="fields"
                 :current-page="currentPage"
                 :per-page="perPage"
                 :filter="filter"
                 :filter-included-fields="['customerNo', 'name']"
                 :sort-by.sync="sortBy"
                 :sort-desc.sync="sortDesc"
                 :sort-direction="sortDirection"
                 :busy="isBusy"
                 stacked="sm"
                 show-empty
                 small
                 @filtered="onFiltered"
                 @context-changed="onContextChanged">
            <template #cell(customerNo)="row">
                <div class="overflow-hidden">
                    {{ row.item.customerNo }}
                </div>
            </template>
            <template #cell(name)="row">
                <div class="overflow-hidden">
                    {{ row.item.name }}
                </div>
            </template>           
            <template #cell(created)="row">
                <div class="overflow-hidden">
                    {{ row.item.displayCreated }}
                </div>
            </template>
            <template #cell(actions)="row">
                <!-- Open sidebar. -->
                <font-awesome-icon class="d-none d-sm-inline text-secondary"
                                   icon="pen-square"
                                   size="lg"
                                   role="button"
                                   title="Redigera kund"
                                   @click="edit(row.item)" />

                <b-button class="text-center d-block d-sm-none"
                          block
                          @click="edit(row.item)">
                    Mer
                </b-button>
                <!-- ./ Open sidebar. -->
                <!-- Select customer. -->
                <TableToggleItem @onToggled="selectCustomer"
                                 :isDisabled="isSubmitting"
                                 :selectedId="selectedCustomerId"
                                 :itemId="row.item.id"
                                 :selectText="'Vald kund'"
                                 :selectedText="'Välj kund'" />
                <!-- ./ Select customer. -->
            </template>
            <template #empty="">
                <h4 class="text-center">Inga resultat</h4>
            </template>
            <template #emptyfiltered="">
                <div class="text-center">
                    <h4>Inga resultat</h4>
                    <h6>
                        <a href="#" @click="filter = ''">
                            Klicka här för att rensa aktiv filtrering
                        </a>
                    </h6>
                </div>
            </template>
            <template #table-busy>
                <div class="text-center mt-4">
                    <font-awesome-icon class="align-middle"
                                       icon="spinner"
                                       size="2x"
                                       spin />
                </div>
            </template>
        </b-table>

        <!--SIDEBAR-->
        <GenericSidebar :visible.sync="sidebar.visible"
                        :is-edit-mode.sync="sidebar.isEditMode"
                        :fields="[
                            { key: 'customerNo', label: 'Kundnr' },
                            { key: 'name', label: 'Namn' },
                            { key: 'displayCustomerType', label: 'Typ' },                           
                            { key: 'displayCreated', label: 'Skapad' }
                          ]"
                        :item="sidebar.selected"
                        @update:updateData="getData">
            <template v-slot:header>
                <span v-if="sidebar.selected">
                    {{ sidebar.selected.name }}
                </span>
            </template>
            <template v-slot:edit>
                <b-button v-if="!sidebar.isEditMode"
                          variant="secondary"
                          class="mb-2"
                          block
                          @click="sidebar.isEditMode = true">
                    Ändra
                </b-button>
                <SaveCustomer v-if="sidebar.isEditMode" :id="sidebar.selected.id" />
                <b-button v-if="!sidebar.isEditMode"
                          variant="danger"
                          class="mb-2"
                          block
                          @click="confirm('removeUser', sidebar.selected.id, `Du kommer ta bort kunden '${sidebar.selected.name}'`, )">
                    Ta bort
                </b-button>
            </template>
        </GenericSidebar>

        <CopyTemplateCustomer :isOpen="copyTemplateCustomer.isOpen" @close="onCopyTemplateCustomerClose" />
    </div>
</template>

<script>
    import { mapActions, mapState, mapGetters } from 'vuex';
    import { get, remove } from '@/helpers/api';
    import SaveCustomer from '@/views/admin/manage/customer/SaveCustomer';
    import GenericSidebar from '@/components/GenericSidebar';
    import PaginationPageSizeSelect from '@/components/PaginationPageSizeSelect';
    import TableToggleItem from '@/components/TableToggleItem';
    import CopyTemplateCustomer from '@/components/customer/CopyTemplateCustomer';

    export default {
        name: 'ListCustomers',
        components: {
            GenericSidebar,
            SaveCustomer,
            PaginationPageSizeSelect,
            TableToggleItem,
            CopyTemplateCustomer
        },
        data() {
            return {
                copyTemplateCustomer: {
                    isOpen: false,
                },
                isSubmitting: false,
                sidebar: {
                    selected: null,
                    visible: false,
                    isEditMode: false
                },
                customers: [],
                fields: [
                    {
                        key: 'customerNo',
                        label: 'Kundnr',
                        sortable: true,
                        sortDirection: 'desc'
                    },
                    { key: 'name', label: 'Namn', sortable: true, sortDirection: 'desc' },                    
                    {
                        key: 'displayCustomerType',
                        label: 'Typ',
                        sortable: true,
                        sortDirection: 'desc'
                    },                    
                    {
                        key: 'created',
                        label: 'Skapad',
                        sortable: true,
                        sortDirection: 'desc'
                    },
                    { key: 'actions', label: '', tdClass: 'table-list--actions--wrapper' }
                ],
                totalRows: 1,
                currentPage: 1,
                perPage: 5,
                sortBy: '',
                sortDesc: false,
                sortDirection: 'asc',
                filter: null,
                isBusy: true
            };
        },
        computed: {
            ...mapState({
                user: (state) => state.user
            }),
            ...mapGetters('user', ['isInSomeRole']),
            isSuperAdmin() {
                return this.isInSomeRole(['SuperAdmin']);
            },
            selectedCustomerId() {
                return !this.user ? null : this.user.selectedCustomer ? this.user.selectedCustomer.id : null;
            },
            sortOptions() {
                // Create an options list from our fields.
                return this.fields
                    .filter((f) => f.sortable)
                    .map((f) => {
                        return { text: f.label, value: f.key };
                    });
            }
        },
        async mounted() {
            // Fetch data.
            await this.getData();
        },
        methods: {
            ...mapActions('user', ['setCustomer']),
            selectCustomer(customerId) {
                this.isSubmitting = true;
                this.setCustomer(this.customers.find(x => x.id === customerId)).then(() => {
                    this.getData();
                    this.isSubmitting = false;
                }).catch(() => this.isSubmitting = false);
            },
            onContextChanged(ctx) {
                if (ctx.perPage > this.totalRows)
                    this.currentPage = 1;
            },
            add() {
                this.sidebar = {
                    selected: {
                        id: '0'
                    },
                    visible: true,
                    isEditMode: true
                };
            },
            edit(item) {
                this.sidebar.selected = item;
                this.sidebar.visible = true;
            },
            onFiltered(filteredItems) {
                // Trigger pagination to update the number of buttons/pages due to filtering
                this.totalRows = filteredItems.length;
            },
            async getData() {
                await get('Customer', 'GetAll')
                    .then((x) => {
                        this.customers = x.data;

                        // Set the initial number of items.
                        this.totalRows = this.customers.length;

                        this.isBusy = false;
                    })
                    .catch((x) => {
                        this.$router.push({
                            name: 'error',
                            params: {
                                statusCode: null,
                                statusText: encodeURIComponent('Något gick snett')
                            }
                        });
                    });
            },
            async removeUser(id) {
                await remove('customer', `Delete?id=${id}`)
                    .then((x) => {
                        // Reset and close sidebar.
                        this.sidebar.visible = false;
                        this.sidebar.selected = {
                            id: 0
                        };
                        // Remove item from list.
                        this.customers.splice(
                            this.customers.findIndex(x => x.id === id), 1
                        );
                        // Update total rows.
                        this.totalRows = this.customers.length;
                    })
                    .catch((x) => {
                        alert('Det gick inte ta bort kunden. Försök igen.');
                    });
            },
            confirm(action, data, message) {
                this.$bvModal
                    .msgBoxConfirm(message, {
                        title: 'Är du säker?',
                        size: 'sm',
                        cancelVariant: 'primary',
                        cancelTitle: 'Nej',
                        okTitle: 'Ja',
                        okVariant: 'danger',
                        footerClass: 'p-2 justify-content-between',
                        hideHeaderClose: false,
                        centered: true
                    })
                    .then((confirmed) => {
                        if (confirmed) this[action](data);
                    })
                    .catch((err) => {
                        // An error occurred
                    });
            },
            addFromTemplate() {
                this.copyTemplateCustomer.isOpen = true;
            },
            async onCopyTemplateCustomerClose() {
                this.copyTemplateCustomer.isOpen = false;
                await this.getData();
            },
        }
    };
</script>
